import { createContext, useEffect, useState } from "react";
import keycloak from "./keycloakClient";

/**
 * Context que expone el estado de autenticación + las funciones para cambiarlo.
 * Se consume desde cualquier componente con useAuth().
 */
export const AuthContext = createContext(null);

/**
 * Flags a nivel de módulo: keycloak.init() solo se puede llamar UNA vez
 * globalmente. React 18 Strict Mode dispara los efectos dos veces en dev
 * para detectar bugs de cleanup; sin estos flags, el init() rompería.
 *
 * Están fuera del componente intencionalmente — sobreviven entre re-mounts.
 *
 * - initStarted: true cuando ya llamamos keycloak.init() (evita dobles inits)
 * - initResolved: true cuando el init terminó (sabemos el estado real)
 *
 * Necesitamos AMBOS: en el segundo mount de Strict Mode, si initStarted=true
 * pero initResolved=false, NO debemos cambiar status — todavía estamos
 * esperando el resultado. Sin esta protección, se ve un flash visible de
 * "unauthenticated" antes de que init resuelva como "authenticated".
 */
let initStarted = false;
let initResolved = false;

/**
 * Provider que envuelve la app y maneja todo el ciclo de auth de Keycloak.
 *
 * Estado expuesto:
 *   status:    'initializing' | 'authenticated' | 'unauthenticated' | 'error'
 *   user:      datos del usuario (sub, email, preferred_username, etc.) o null
 *   token:     JWT actual (string) o undefined
 *   error:     mensaje de error si status === 'error'
 *   login():   redirige a la pantalla de login de Keycloak
 *   logout():  cierra sesión y redirige a Keycloak para invalidar la SSO session
 *   getToken(): devuelve el token actual, refrescándolo si está por expirar
 */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState("initializing");
  const [error, setError] = useState(null);
  // Trigger de re-render cuando los tokens cambian (refresh, login, logout).
  // El valor en sí no importa — solo incrementa para forzar render.
  const [tick, setTick] = useState(0);
  const rerender = () => setTick((t) => t + 1);

  useEffect(() => {
    // Strict Mode protection: el init solo se ejecuta una vez globalmente.
    if (initStarted) {
      // Si ya empezó pero AÚN no resolvió, no cambiamos status — el primer
      // init en vuelo lo seteará correctamente cuando termine. Si cambiáramos
      // aquí, se vería un flash de "unauthenticated" antes del estado real.
      if (initResolved) {
        setStatus(keycloak.authenticated ? "authenticated" : "unauthenticated");
      }
      return;
    }
    initStarted = true;

    // Callbacks de Keycloak: el SDK los dispara cuando suceden eventos.
    // Esto incluye refresh exitoso, logout en otra pestaña, token expirado, etc.
    keycloak.onAuthSuccess = () => {
      setStatus("authenticated");
      rerender();
    };
    keycloak.onAuthError = (err) => {
      setError(err?.error_description || "Error de autenticación");
      setStatus("error");
    };
    keycloak.onAuthRefreshSuccess = () => rerender();
    keycloak.onAuthRefreshError = () => {
      // Refresh falló → la sesión SSO probablemente expiró. Forzar login.
      setStatus("unauthenticated");
    };
    keycloak.onAuthLogout = () => {
      setStatus("unauthenticated");
      rerender();
    };
    keycloak.onTokenExpired = () => {
      // Intenta refrescar con 30s de margen antes de que realmente expire.
      keycloak.updateToken(30).catch(() => setStatus("unauthenticated"));
    };

    keycloak
      .init({
        // 'login-required': si no hay sesión, redirige directo a Keycloak
        // (top-level redirect, sin iframes). Cuando volvemos con el code en la
        // URL, keycloak-js lo procesa y quedamos authenticated.
        //
        // Antes usábamos 'check-sso' + silentCheckSsoRedirectUri, pero eso
        // carga un iframe hidden hacia auth.one-perce.net → los browsers
        // bloquean cookies third-party ahí → timeout + loop de redirect.
        // Con 'login-required' no hay iframe, y el trade-off es que el user
        // ve el flow de Keycloak en pantalla completa cada vez que carga sin
        // sesión (aceptable — es más honesto además).
        onLoad: "login-required",
        pkceMethod: "S256",
        // Iframe polling también desactivado por la misma razón (cross-origin
        // + third-party cookies). Sin él, un logout en otra pestaña no se
        // propaga; el token expira y el user vuelve a login. Trade-off estándar.
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        initResolved = true;
        setStatus(authenticated ? "authenticated" : "unauthenticated");
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Keycloak init failed", err);
        initResolved = true;
        setError(err?.message || "No se pudo conectar con el servidor de autenticación");
        setStatus("error");
      });
  }, []);

  // Valor que el Provider expone al árbol de componentes.
  // Se recalcula en cada render (cheap, son solo referencias).
  const value = {
    status,
    error,
    user: keycloak.tokenParsed || null,
    token: keycloak.token,
    login: () =>
      keycloak.login({
        redirectUri: window.location.origin,
      }),
    logout: () =>
      keycloak.logout({
        redirectUri: window.location.origin,
      }),
    /**
     * Devuelve el token actual, refrescándolo si está a punto de expirar.
     * Llama esta función justo antes de cada fetch al backend para garantizar
     * que mandes un token fresco.
     */
    getToken: async (minValiditySeconds = 30) => {
      await keycloak.updateToken(minValiditySeconds);
      return keycloak.token;
    },
  };
  // El parámetro `tick` no se usa directamente; existe solo para que React
  // re-renderice cuando los tokens cambien y el `value` traiga datos frescos.
  void tick;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
